import React, { useCallback, useRef, useState } from 'react'
import type { ScheduleActivity, ScheduleCalendar, ScheduleDependency, ScheduleExternalActivity } from '../types'
import GanttTooltip from './GanttTooltip'
import { createHoverStore, GanttHoverDispatchContext, GanttHoverStateContext, GanttHoverStoreContext, type HoverState, type HoverStore } from './gantt-hover'

interface Props {
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  dependencies: ScheduleDependency[]
  /** Review fix (Important #3): forwarded to `GanttTooltip` for the working-day variance calc. */
  calendarsById: Map<string, ScheduleCalendar>
  defaultCalendarId: string
  children: React.ReactNode
}

/**
 * Owns the hover state itself so a hover change re-renders only this provider and
 * `GanttTooltip` — never `children`. Task 10 fix round 1: the state used to live
 * directly in `GanttView`, so every `setHover` re-rendered `GanttView`, and because
 * `GanttTimeline`/`DependencyLayer` are elements `GanttView` instantiates inline in
 * its own JSX, a `GanttView` re-render recreated and re-rendered them regardless of
 * which hover context they subscribed to — the state/dispatch context split alone
 * didn't stop it. Here, `children` is a prop: as long as `GanttView` itself doesn't
 * re-render, the same `children` element reference flows back out of this
 * component's return on every hover change, and React bails out of reconciling an
 * unchanged element reference — so `GanttTimeline` never re-renders from a hover.
 */
const GanttHoverProvider: React.FC<Props> = ({ byId, externalById, dependencies, calendarsById, defaultCalendarId, children }) => {
  const [hover, setHoverState] = useState<HoverState>(null)
  // Task 18: mirrors `hover` into a tiny external store (see gantt-hover.ts) so
  // `useHoveredActivityId` can subscribe with per-id precision instead of re-rendering on every
  // hover change. Lazily created once via the ref-init pattern (a plain `useRef(createHoverStore())`
  // would call the factory on every render).
  const storeRef = useRef<HoverStore | null>(null)
  if (!storeRef.current) storeRef.current = createHoverStore()
  const setHover = useCallback((h: HoverState) => {
    setHoverState(h)
    storeRef.current!.set(h)
  }, [])
  return (
    <GanttHoverStateContext.Provider value={hover}>
      <GanttHoverDispatchContext.Provider value={setHover}>
        <GanttHoverStoreContext.Provider value={storeRef.current}>
          {children}
          <GanttTooltip byId={byId} externalById={externalById} dependencies={dependencies} calendarsById={calendarsById} defaultCalendarId={defaultCalendarId} />
        </GanttHoverStoreContext.Provider>
      </GanttHoverDispatchContext.Provider>
    </GanttHoverStateContext.Provider>
  )
}

export default GanttHoverProvider
